// Factory function pattern to avoid Webpack TDZ issues with class inheritance
// This uses BaseNotaryFactory instead of class inheritance

import { createBaseNotary, type IBaseNotary } from "../BaseNotaryFactory";
import type { Deed } from "le-coffre-resources/dist/Notary";
import type { QueryPayload } from "../../../types";
import type { IGetDeedsParams, IPutDeedsParams } from "./Deeds";
import { buildDeedsUpdatePayload } from "@Front/Api/LeCoffreApi/helpers/businessRequestPayloadHelpers";

interface DeedsInstance {
	get(q: IGetDeedsParams): Promise<Deed[]>;
	getByUid(uid: string, q?: QueryPayload): Promise<Deed>;
	put(uid: string, body: IPutDeedsParams): Promise<Deed>;
}

// Module-level singleton instance
let instance: DeedsInstance | null = null;
let baseNotaryInstance: IBaseNotary | null = null;

function createDeeds(): DeedsInstance {
	if (!baseNotaryInstance) {
		baseNotaryInstance = createBaseNotary();
	}

	const baseNotary = baseNotaryInstance;
	const baseUrl = baseNotary.namespaceUrl.concat("/deeds");

	return {
		get: async (q: IGetDeedsParams): Promise<Deed[]> => {
			const url = new URL(baseUrl);
			baseNotary.appendJsonSearchParams(url, { q });
			try {
				return await baseNotary.getRequest<Deed[]>(url);
			} catch (err) {
				baseNotary.onError(err);
				return Promise.reject(err);
			}
		},
		getByUid: async (uid: string, q?: QueryPayload): Promise<Deed> => {
			const url = new URL(baseUrl.concat(`/${uid}`));
			if (q) baseNotary.appendJsonSearchParams(url, q);
			try {
				return await baseNotary.getRequest<Deed>(url);
			} catch (err) {
				baseNotary.onError(err);
				return Promise.reject(err);
			}
		},
		put: async (uid: string, body: IPutDeedsParams): Promise<Deed> => {
			const url = new URL(baseUrl.concat(`/${uid}`));
			try {
				const payload = buildDeedsUpdatePayload(body);
				return await baseNotary.putRequest<Deed>(url, payload);
			} catch (err) {
				baseNotary.onError(err);
				return Promise.reject(err);
			}
		},
	};
}

export function getDeedsInstance(): DeedsInstance {
	if (!instance) {
		instance = createDeeds();
	}
	return instance;
}
